import useSwr from 'swr'
import { API_URL } from '@/utils/constants'
import { isCollectionObjectSlug } from '@/utils/collection-object'

interface TraitValueEntry {
	trait_type: string
	value: string
	count: number
}
interface TraitGroup {
	trait_type: string
	values: TraitValueEntry[]
}

export interface CollectionRarityData {
	totalSupply?: number
	map: Record<string, Record<string, number>>
}

const SKIP_SLUGS = new Set(['%5Bid%5D', 'sub-100k', 'sub-1k'])

const fetcher = async ([_k, slug]: [string, string]): Promise<CollectionRarityData | null> => {
	if (!slug || SKIP_SLUGS.has(slug)) return null
	if (isCollectionObjectSlug(slug)) return null
	const attrsRes = await fetch(`${API_URL}/collection/${encodeURIComponent(slug)}/attributes`)
	if (!attrsRes.ok) return null
	const statsRes = await fetch(`${API_URL}/collection/${encodeURIComponent(slug)}/stats`)
	const [attrs, stats] = await Promise.all([
		attrsRes.json().catch(() => null),
		statsRes.ok ? statsRes.json().catch(() => null) : null
	])
	const totalSupply: number | undefined = stats?.total_supply
	if (!Array.isArray(attrs)) return { totalSupply, map: {} }

	const map: Record<string, Record<string, number>> = {}
	;(attrs as TraitGroup[]).forEach((group) => {
		const inner: Record<string, number> = {}
		group.values?.forEach((v) => {
			inner[String(v.value).toLowerCase()] = v.count
		})
		map[group.trait_type.toLowerCase()] = inner
	})
	return { totalSupply, map }
}

export default function useCollectionRarity(slug?: string) {
	return useSwr<CollectionRarityData | null>(
		slug ? ['collection-rarity', slug] : null,
		fetcher,
		{ revalidateOnFocus: false, dedupingInterval: 5 * 60_000 }
	)
}

export function getRarityRatio(
	data: CollectionRarityData | null | undefined,
	trait_type: string,
	value: any
): number | null {
	if (!data?.map || !data.totalSupply) return null
	const traitMap = data.map[trait_type.toLowerCase()]
	if (!traitMap) return null
	const count = traitMap[String(value).toLowerCase()]
	if (typeof count !== 'number') return null
	return (count / data.totalSupply) * 100
}

export function formatRarityPercent(pct: number): string {
	if (pct >= 10) return `${pct.toFixed(1)}%`
	if (pct >= 1) return `${pct.toFixed(2)}%`
	return `${pct.toFixed(3)}%`
}

type RarityTier = 'exotic' | 'legendary' | 'epic' | 'rare' | 'uncommon' | 'common'

export interface RarityTierStyle {
	tier: RarityTier
	label: string
	// Full pill: muted colored background, vivid colored text (light + dark variants)
	className: string
	// Plain text colour only — for inline tier-coloured labels (no pill chrome).
	// Works in both light and dark modes.
	textClassName: string
}

// Thresholds (percent of supply). Lower percent = rarer.
// `className` is the muted/glow variant used in DARK mode.
// `lightClassName` is the bold solid-gradient variant used in LIGHT mode
// (matches the bottom row of the reference design — vivid pill, white text).
const TIER_THRESHOLDS: Array<{
	tier: RarityTier
	max: number
	label: string
	className: string
	lightClassName: string
	textClassName: string
}> = [
	{
		tier: 'exotic',
		max: 1,
		label: 'Exotic',
		className:
			'dark:bg-gradient-to-r dark:from-violet-500/20 dark:via-fuchsia-500/15 dark:to-purple-500/20 dark:text-violet-200 dark:border dark:border-violet-400/40 dark:shadow-[0_0_12px_rgba(167,139,250,0.25)]',
		lightClassName:
			'bg-gradient-to-r from-violet-400 via-fuchsia-400 to-purple-500 text-white shadow-[0_0_12px_rgba(167,139,250,0.45)]',
		textClassName: 'text-violet-600 dark:text-violet-300'
	},
	{
		tier: 'legendary',
		max: 5,
		label: 'Legendary',
		className:
			'dark:bg-gradient-to-r dark:from-amber-500/20 dark:to-yellow-500/15 dark:text-amber-200 dark:border dark:border-amber-400/40 dark:shadow-[0_0_10px_rgba(252,211,77,0.18)]',
		lightClassName:
			'bg-gradient-to-r from-amber-400 to-yellow-400 text-amber-950 shadow-[0_0_10px_rgba(252,211,77,0.35)]',
		textClassName: 'text-amber-600 dark:text-amber-300'
	},
	{
		tier: 'epic',
		max: 15,
		label: 'Epic',
		className:
			'dark:bg-gradient-to-r dark:from-rose-500/20 dark:to-pink-500/15 dark:text-pink-200 dark:border dark:border-pink-400/40 dark:shadow-[0_0_10px_rgba(244,114,182,0.15)]',
		lightClassName:
			'bg-gradient-to-r from-indigo-500 via-purple-500 to-violet-600 text-white shadow-[0_0_10px_rgba(139,92,246,0.4)]',
		textClassName: 'text-violet-600 dark:text-pink-300'
	},
	{
		tier: 'rare',
		max: 25,
		label: 'Rare',
		className:
			'dark:bg-gradient-to-r dark:from-emerald-500/20 dark:to-green-500/15 dark:text-emerald-200 dark:border dark:border-emerald-400/40 dark:shadow-[0_0_8px_rgba(110,231,183,0.12)]',
		lightClassName:
			'bg-gradient-to-r from-emerald-300 to-green-400 text-emerald-950 shadow-[0_0_8px_rgba(110,231,183,0.35)]',
		textClassName: 'text-emerald-600 dark:text-emerald-300'
	},
	{
		tier: 'uncommon',
		max: 50,
		label: 'Uncommon',
		className:
			'dark:bg-gradient-to-r dark:from-sky-500/20 dark:to-blue-500/15 dark:text-sky-200 dark:border dark:border-sky-400/40 dark:shadow-[0_0_8px_rgba(125,211,252,0.12)]',
		lightClassName:
			'bg-gradient-to-r from-sky-300 to-blue-400 text-sky-950 shadow-[0_0_8px_rgba(125,211,252,0.35)]',
		textClassName: 'text-sky-600 dark:text-sky-300'
	},
	{
		tier: 'common',
		max: Infinity,
		label: 'Common',
		className: 'dark:bg-zinc-500/15 dark:text-zinc-300 dark:border dark:border-zinc-500/40',
		lightClassName: 'bg-zinc-400 text-white',
		textClassName: 'text-zinc-500 dark:text-zinc-400'
	}
]

export function getRarityTier(pct: number | null | undefined): RarityTierStyle | null {
	if (pct == null) return null
	const match = TIER_THRESHOLDS.find((t) => pct <= t.max) ?? TIER_THRESHOLDS[TIER_THRESHOLDS.length - 1]
	// Apply the light-mode (solid gradient) classes by default; dark-mode classes
	// in `match.className` use `dark:` prefixes to override when html.dark is set.
	return {
		tier: match.tier,
		label: match.label,
		className: `${match.lightClassName} ${match.className}`,
		textClassName: match.textClassName
	}
}
